import { useEffect, useState, createContext, useContext, useRef } from 'react';
import { useNavigation, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { DEFAULT_LANDING_PATH } from './featureFlags';

// Setting up an auth context to be used globally
const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

// Outseta is added to the window by the Outseta script added to the head in ../public/index.html
export function getOutseta() {
  if (window['Outseta']) {
    return window['Outseta'];
  } else {
    throw new Error('Outseta is missing, have you added the script to head?');
  }
}

export default function AuthProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('init');
  const [user, setUser] = useState();
  const [mongoUser, setMongoUser] = useState(null);
  const [mongoUserId, setMongoUserId] = useState('');
  const navigate = useNavigate();

  // Save a reference to Outseta
  const outsetaRef = useRef(getOutseta());

  useEffect(() => {
    // Get the access token from the callback URL
    const accessToken = searchParams.get('access_token');

    if (accessToken) {
      // If there is an access token present, pass it along to Outseta and clean up
      outsetaRef.current.setAccessToken(accessToken);
      setSearchParams({});
    }

    // Hydrate Outseta from cookie if no token is currently set (e.g., new tab)
    try {
      const cookieToken = Cookies.get('vestmapToken');
      if (!outsetaRef.current.getAccessToken() && cookieToken) {
        outsetaRef.current.setAccessToken(cookieToken);
      }
    } catch (e) {
      // noop: cookie may be unavailable
    }

    // Check if we have a token and validate it before proceeding
    if (outsetaRef.current.getAccessToken()) {
      console.log('[OUTSETA] Token found on app load, validating...');
      // Validate token before proceeding with user update
      isTokenValid().then(isValid => {
        if (isValid) {
          // Token is valid, proceed with user update
          console.log(
            '[OUTSETA] Token valid on app load, proceeding with user update',
          );
          updateUser();
        } else {
          // Token is expired, log out immediately
          console.log('[OUTSETA] Token expired on app load, logging out...');
          performLogout();
        }
      });
    } else {
      console.log(
        '[OUTSETA] No token found on app load, setting status to ready',
      );
      // Outseta initialized without an authenticated user
      setStatus('ready');
    }

    // Check if we have cached MongoDB user data
    const cachedUser = localStorage.getItem('mongodbUser');
    if (cachedUser) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setMongoUser(parsedUser);
        setMongoUserId(parsedUser._id);
      } catch (error) {
        console.log('Error parsing cached MongoDB user:', error);
      }
    }

    return () => {
      // Clean up user-related event subscriptions
      handleOutsetaUserEvents(() => {});
    };
  }, [searchParams, setSearchParams]);

  // JWT token validation function
  const isTokenValid = async () => {
    try {
      const token = outsetaRef.current.getAccessToken();
      if (!token) {
        console.log('[OUTSETA] No token found for validation');
        return false;
      }

      // Decode JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiry = new Date(payload.exp * 1000);
      const timeUntilExpiry = payload.exp - currentTime;

      console.log(
        `[OUTSETA] Token validation - Current time: ${new Date().toISOString()}, Token expires: ${tokenExpiry.toISOString()}, Time until expiry: ${Math.floor(
          timeUntilExpiry / 60,
        )} minutes`,
      );

      // Check if token is expired (not just expiring soon)
      const isValid = payload.exp > currentTime;
      console.log(`[OUTSETA] Token is ${isValid ? 'VALID' : 'EXPIRED'}`);
      return isValid;
    } catch (error) {
      console.error('[OUTSETA] Token validation failed:', error);
      return false;
    }
  };

  // Enhanced logout function that clears all user state
  const performLogout = () => {
    console.log('[OUTSETA] Token expired - logging out user');
    // Clear all tokens and user state
    outsetaRef.current.setAccessToken('');
    Cookies.remove('vestmapToken');
    localStorage.clear();
    setUser(null);
    setMongoUser(null);
    setMongoUserId('');
    setStatus('ready');
    navigate('/');
  };

  // Wrapper function for Outseta API calls with retry logic
  const outsetaApiCall = async (apiFunction, retry = true) => {
    try {
      return await apiFunction();
    } catch (error) {
      if (
        retry &&
        (error.status === 401 ||
          error.message?.includes('401') ||
          error.message?.includes('Unauthorized'))
      ) {
        console.log('401 error detected, refreshing token...');

        // Clear expired token
        outsetaRef.current.setAccessToken('');
        Cookies.remove('vestmapToken');

        // Try to refresh - this might require user interaction
        try {
          outsetaRef.current.auth.open({
            widgetMode: 'login',
            authenticationCallbackUrl: `${window.location.origin}${DEFAULT_LANDING_PATH}`,
          });
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw error; // Re-throw original error
        }

        // Retry the API call once
        return await apiFunction();
      }
      throw error;
    }
  };

  const updateUser = async () => {
    try {
      console.log('[OUTSETA] Updating user, checking token validity...');
      // Check token validity before making API call
      if (!(await isTokenValid())) {
        console.log(
          '[OUTSETA] Token is invalid or expired during user update, logging out...',
        );
        performLogout();
        return;
      }

      console.log('[OUTSETA] Token valid, fetching user data...');
      // Use the wrapper function for API calls
      const outsetaUser = await outsetaApiCall(() =>
        outsetaRef.current.getUser(),
      );
      console.log('[OUTSETA] User data fetched successfully');
      setUser(outsetaUser);
      setStatus('ready');
    } catch (error) {
      console.error('[OUTSETA] Outseta getUser failed:', error);
      setStatus('ready');
      setUser(null);
    }
  };

  useEffect(() => {
    if (status === 'ready' && user) {
      const access_token = outsetaRef.current.getAccessToken();

      Cookies.set('vestmapToken', access_token, {
        expires: 7,
        secure: true,
        sameSite: 'lax',
      });

      // Fetch MongoDB user data after successful Outseta authentication
      fetchMongoUser(user.Uid);
    }
  }, [status, user]);

  // Periodic token validation to prevent 401 errors
  useEffect(() => {
    if (!user) {
      console.log(
        '[OUTSETA] No user found, skipping periodic token validation',
      );
      return;
    }

    console.log(
      '[OUTSETA] Starting periodic token validation (every 2 minutes)',
    );
    // Check token validity every 2 minutes for more responsive logout
    const interval = setInterval(async () => {
      console.log('[OUTSETA] Running periodic token validation...');
      if (user && !(await isTokenValid())) {
        console.log('[OUTSETA] Token expired during session, logging out...');
        performLogout();
      } else {
        console.log('[OUTSETA] Periodic check - Token is valid');
      }
    }, 2 * 60 * 1000); // 2 minutes

    return () => {
      console.log('[OUTSETA] Clearing periodic token validation interval');
      clearInterval(interval);
    };
  }, [user]);

  const fetchMongoUser = async outsettaID => {
    // Skip if we don't have an outsettaID
    if (!outsettaID) return;

    try {
      // Check if we already have cached user data first
      const cachedUser = localStorage.getItem('mongodbUser');
      if (cachedUser) {
        try {
          const parsedUser = JSON.parse(cachedUser);
          setMongoUser(parsedUser);
          setMongoUserId(parsedUser._id);
          return; // Use cached data if available
        } catch (error) {
          console.log(
            'Error parsing cached MongoDB user, fetching fresh data:',
            error,
          );
          // Continue to fetch fresh data if parsing fails
        }
      }

      const url = new URL(
        process.env.REACT_APP_NODE_URL + '/user_data/' + outsettaID,
      );
      const response = await makeAuthenticatedRequest(url);

      if (response.status === 401) {
        // Token expired, perform logout
        console.log(
          '[OUTSETA] 401 error in fetchMongoUser, token expired, logging out...',
        );
        performLogout();
        return;
      }

      if (response.status === 404) {
        navigate('/in-progress');
        return;
      }

      const data = await response.json();
      const { user } = data;
      localStorage.setItem('mongodbUser', JSON.stringify(user));
      setMongoUser(user);
      setMongoUserId(user._id);
    } catch (error) {
      console.log({ error });
    }
  };

  const handleOutsetaUserEvents = onEvent => {
    // Subscribe to user-related events with onEvent function
    const outseta = outsetaRef.current;
    outseta.on('subscription.update', onEvent);
    outseta.on('profile.update', onEvent);
    outseta.on('account.update', onEvent);
  };

  const getAccessToken = () => {
    // Prefer Outseta's in-memory token; fall back to cookie for new tabs
    const tokenFromOutseta = outsetaRef.current.getAccessToken();
    if (tokenFromOutseta) return tokenFromOutseta;
    try {
      const tokenFromCookie = Cookies.get('vestmapToken');
      return tokenFromCookie || null;
    } catch (e) {
      return null;
    }
  };

  // Utility function for making API calls with automatic 401 error handling
  // Usage: const response = await makeAuthenticatedRequest(url, { method: 'POST', body: JSON.stringify(data) });
  // This function automatically handles 401 errors by clearing expired tokens and user state
  const makeAuthenticatedRequest = async (url, options = {}) => {
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);

      if (response.status === 401) {
        console.log('[OUTSETA] 401 error in API request, token may be expired');
        // Perform logout on 401 error
        performLogout();

        // Return a response object that indicates authentication failure
        return {
          ok: false,
          status: 401,
          json: async () => ({
            error: 'Authentication expired. Please log in again.',
          }),
        };
      }

      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('[OUTSETA] User initiated logout');
    performLogout();
  };

  const openLogin = options => {
    outsetaRef.current.auth.open({
      widgetMode: 'login|register',
      authenticationCallbackUrl: `${window.location.origin}${DEFAULT_LANDING_PATH}`,
      ...options,
    });
  };

  const closeChatHead = options => {
    outsetaRef.current.chat.hide();
  };

  const openSignup = options => {
    outsetaRef.current.auth.open({
      widgetMode: 'register',
      planFamilyUid: 'rmkwe1mg',
      authenticationCallbackUrl: `${window.location.origin}/`,
      ...options,
    });
  };

  const openProfile = options => {
    outsetaRef.current.profile.open({ tab: 'profile', ...options });
  };

  const openChangePlan = () => {
    outsetaRef.current.profile.open({
      tab: 'planChange',
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        mongoUser,
        mongoUserId,
        isLoading: status !== 'ready',
        logout,
        openLogin,
        openSignup,
        openProfile,
        openChangePlan,
        closeChatHead,
        getAccessToken,
        makeAuthenticatedRequest,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
